import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '/api';

function SessionReplay({ sessions, lpUrl, lpQuery }) {
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const [clickMarkers, setClickMarkers] = useState([]);

  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const startTimeRef = useRef(null);
  const containerRef = useRef(null);

  const loadSession = async (sessionId) => {
    setSelectedSession(sessionId);
    setPlaying(false);
    setProgress(0);
    setCursorPos({ x: -100, y: -100 });
    setClickMarkers([]);

    try {
      const res = await fetch(`${API}/data/sessions/${sessionId}${lpQuery || ''}`);
      const data = await res.json();
      setSessionData(data);
    } catch (e) {
      console.error('Failed to load session:', e);
    }
  };

  const getScale = useCallback(() => {
    if (!sessionData || !sessionData.page || !containerRef.current) return 1;
    return containerRef.current.clientWidth / (sessionData.page.width || 1440);
  }, [sessionData]);

  const play = useCallback(() => {
    if (!sessionData || !sessionData.events || sessionData.events.length === 0) return;

    const events = sessionData.events;
    const startTs = events[0].timestamp;
    const endTs = events[events.length - 1].timestamp;
    const duration = endTs - startTs;

    if (duration === 0) return;

    setPlaying(true);
    startTimeRef.current = Date.now() - (progress / 100) * (duration / speed);

    const animate = () => {
      const elapsed = (Date.now() - startTimeRef.current) * speed;
      const currentTs = startTs + elapsed;
      const pct = Math.min((elapsed / duration) * 100, 100);

      setProgress(pct);

      const scale = getScale();

      // Find the latest move/click event up to currentTs
      let lastMove = null;
      const newClicks = [];

      for (const event of events) {
        if (event.timestamp > currentTs) break;
        if (event.type === 'move') {
          lastMove = event;
        }
        if (event.type === 'click' && currentTs - event.timestamp < 500) {
          newClicks.push({ x: event.x * scale, y: event.y * scale, id: event.timestamp });
        }
      }

      if (lastMove) {
        setCursorPos({ x: lastMove.x * scale, y: lastMove.y * scale });
      }
      setClickMarkers(newClicks);

      if (pct >= 100) {
        setPlaying(false);
        return;
      }

      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);
  }, [sessionData, progress, speed, getScale]);

  const pause = () => {
    setPlaying(false);
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
    }
  };

  const reset = () => {
    pause();
    setProgress(0);
    setCursorPos({ x: -100, y: -100 });
    setClickMarkers([]);
  };

  useEffect(() => {
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  // Draw session path on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sessionData || !sessionData.events) return;

    const scale = getScale();
    const pageHeight = sessionData.page ? sessionData.page.height : 900;

    canvas.width = canvas.parentElement.clientWidth;
    canvas.height = pageHeight * scale;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Transparent background - LP iframe shows through
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw movement path
    const moves = sessionData.events.filter(e => e.type === 'move');
    if (moves.length > 1) {
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(88, 166, 255, 0.15)';
      ctx.lineWidth = 1;
      ctx.moveTo(moves[0].x * scale, moves[0].y * scale);
      for (let i = 1; i < moves.length; i++) {
        ctx.lineTo(moves[i].x * scale, moves[i].y * scale);
      }
      ctx.stroke();
    }

    // Draw click positions
    const clicks = sessionData.events.filter(e => e.type === 'click');
    clicks.forEach((click, i) => {
      ctx.beginPath();
      ctx.arc(click.x * scale, click.y * scale, 8, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(248, 81, 73, 0.4)';
      ctx.fill();
      ctx.strokeStyle = '#f85149';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = '#fff';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(i + 1, click.x * scale, click.y * scale + 3);
    });

  }, [sessionData, getScale]);

  if (sessions.length === 0) {
    return (
      <div className="empty-state">
        <div className="icon">▶️</div>
        <h3>セッションデータがありません</h3>
        <p>ユーザーがページを操作するとセッションが記録されます</p>
      </div>
    );
  }

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('ja-JP');
  };

  return (
    <div>
      <div className="session-list">
        {sessions.map(s => (
          <div
            key={s.sessionId}
            className={`session-item ${selectedSession === s.sessionId ? 'active' : ''}`}
            onClick={() => loadSession(s.sessionId)}
          >
            <div className="session-info">
              <span className="session-id">セッション: {s.sessionId}</span>
              <span className="session-meta">
                {s.eventCount} イベント | {formatTime(s.updatedAt)}
              </span>
            </div>
            <span style={{ color: '#58a6ff', fontSize: 20 }}>▶</span>
          </div>
        ))}
      </div>

      {sessionData && (
        <div>
          <div className="replay-container" ref={containerRef} style={{ maxHeight: 600, overflow: 'auto' }}>
            <iframe
              src={lpUrl || '/api/lp'}
              title="LP Background"
              style={{
                width: '100%',
                height: sessionData.page ? sessionData.page.height * getScale() : 900,
                border: 'none',
                pointerEvents: 'none',
                position: 'absolute',
                top: 0,
                left: 0,
                opacity: 0.5,
              }}
            />
            <canvas ref={canvasRef} className="replay-canvas" style={{ position: 'relative', zIndex: 1 }} />
            <div
              className="replay-cursor"
              style={{ left: cursorPos.x, top: cursorPos.y, zIndex: 2 }}
            />
            {clickMarkers.map(c => (
              <div
                key={c.id}
                className="replay-click-marker"
                style={{ left: c.x, top: c.y, zIndex: 2 }}
              />
            ))}
          </div>
          <div className="replay-controls">
            {!playing ? (
              <button onClick={play}>再生</button>
            ) : (
              <button onClick={pause}>一時停止</button>
            )}
            <button onClick={reset} style={{ background: '#2d333b', color: '#e1e4e8' }}>リセット</button>
            <div className="replay-progress">
              <div className="replay-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="replay-time">{Math.round(progress)}%</span>
            <div className="speed-control">
              <span>速度:</span>
              <select value={speed} onChange={e => setSpeed(Number(e.target.value))}>
                <option value={0.5}>0.5x</option>
                <option value={1}>1x</option>
                <option value={2}>2x</option>
                <option value={4}>4x</option>
                <option value={8}>8x</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SessionReplay;
